import { useEffect, useRef, useState } from 'react';
import { sAddr } from '../lib/utils';
import { useAuth } from '../context/AuthContext';
import { fetchDAProDetail, proDataCache } from '../lib/fetchDAProDetail';
import { reportClientDiagnostic } from '../lib/clientDiagnostics';
import { trackedStageValue as pipelineValue } from '../lib/trackedPipelineSummary';

const PIPELINE_COLORS = {
  watching:  { bg: "#1a2840", text: "#a8b8cc", dot: "#a8b8cc", label: "Watching" },
  contacted: { bg: "#1e3a5f", text: "#3b82f6", dot: "#3b82f6", label: "Contacted" },
  quoted:    { bg: "#2a2418", text: "#d4a84a", dot: "#d4a84a", label: "Quoted" },
  won:       { bg: "#142e20", text: "#5aba6a", dot: "#5aba6a", label: "Won" },
  lost:      { bg: "#2e1a20", text: "#d06868", dot: "#d06868", label: "Lost" },
  not_relevant: { bg: "#242b36", text: "#8ea0b8", dot: "#8ea0b8", label: "Not relevant" },
};

const CORE_STAGE_ORDER = ['watching', 'contacted', 'quoted', 'won'];
const SECONDARY_STAGE_ORDER = ['lost', 'not_relevant'];
const STAGE_ORDER = [...CORE_STAGE_ORDER, ...SECONDARY_STAGE_ORDER];

function fmt(n) {
  if (!n || n === 0) return null;
  if (n >= 1000000) return `$${(n / 1000000).toFixed(1)}M`;
  if (n >= 1000) return `$${(n / 1000).toFixed(0)}K`;
  return `$${Number(n).toLocaleString()}`;
}

function daysInStage(da) {
  if (!da.pipeline_updated_at) return null;
  const d = Math.floor((Date.now() - new Date(da.pipeline_updated_at)) / 86400000);
  if (d === 0) return 'today';
  if (d === 1) return '1d';
  return `${d}d`;
}

function followUpMeta(dateStr) {
  if (!dateStr) return null;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const date = new Date(`${dateStr}T00:00:00`);
  const days = Math.round((date - today) / 86400000);
  if (days < 0) return { label: `${Math.abs(days)}d overdue`, urgent: true };
  if (days === 0) return { label: 'Due today', urgent: true };
  if (days === 1) return { label: 'Due tomorrow', urgent: false };
  return { label: `Due in ${days}d`, urgent: false };
}

function nextStageFor(stage) {
  if (stage === 'watching') return 'contacted';
  if (stage === 'contacted') return 'quoted';
  if (stage === 'quoted') return 'won';
  return null;
}

function ProSignal({ da, user }) {
  const cacheScope = user?.id || 'user';
  const cacheKey = `${cacheScope}:${da.id}`;
  const [proData, setProData] = useState(() => proDataCache.get(cacheKey) || null);

  useEffect(() => {
    if (!da.id || proData) return;
    let cancelled = false;
    fetchDAProDetail(da.id, { cacheScope, timeoutMs: 10000, recordCircuitFailure: false })
      .then(data => {
        if (!cancelled) setProData(data);
      })
      .catch(error => {
        reportClientDiagnostic('pipeline_pro_signal_failed', {
          severity: 'warning',
          user,
          surface: 'pipeline_summary',
          page: 'tracked',
          da,
          message: error.message || 'Pipeline Pro signal failed to load',
          error,
        });
      });
    return () => {
      cancelled = true;
    };
  }, [cacheScope, da, da.id, proData, user]);

  const parties = proData?.trusted_parties || [];
  const roles = parties
    .map(p => (p.role || '').toLowerCase())
    .filter(Boolean);
  const hasContact = parties.some(p =>
    p.email || p.phone || p.website || p.company_phone || p.company_email || p.company_website
  );

  const badges = [];
  if (roles.some(r => r.includes('builder'))) badges.push('Builder');
  if (roles.some(r => r.includes('architect'))) badges.push('Architect');
  if (roles.some(r => r.includes('planner') || r.includes('certifier') || r.includes('engineer') || r.includes('surveyor'))) badges.push('Specialist');
  if (hasContact) badges.push('Contact');
  if (badges.length === 0 && parties.length > 0) badges.push('Pro intel');

  if (badges.length === 0) return null;

  return (
    <div style={{ display: 'flex', gap: 5, flexWrap: 'wrap', marginTop: 7 }}>
      {badges.slice(0, 4).map(label => (
        <span key={label} style={{
          fontSize: 10, fontWeight: 700, color: '#d4a84a',
          border: '1px solid #d4a84a44', background: '#d4a84a12',
          borderRadius: 999, padding: '2px 7px',
        }}>
          {label}
        </span>
      ))}
    </div>
  );
}

// ── Reset confirmation sheet ──────────────────────────────────────────────────
function ResetSheet({ da, updateStage, onClose }) {
  const [saving, setSaving] = useState(false);

  const confirm = async () => {
    if (saving) return;
    setSaving(true);
    await updateStage(da.tracked_id, da.pipeline_stage || 'watching', 'watching', {
      quoted_amount:  null,
      won_amount:     null,
      contact_name:   null,
      contact_method: null,
      lost_reason:    null,
      next_follow_up_at: null,
      follow_up_reason: null,
      last_contacted_at: null,
    });
    setSaving(false);
    onClose();
  };

  return (
    <div
      onClick={onClose}
      style={{ position: 'fixed', inset: 0, zIndex: 2000, background: 'rgba(0,0,0,0.65)', display: 'flex', alignItems: 'flex-end' }}
    >
      <style>{`@keyframes slideUp { from { transform: translateY(60px); opacity: 0; } to { transform: translateY(0); opacity: 1; } }`}</style>
      <div
        onClick={e => e.stopPropagation()}
        style={{
          width: '100%', maxWidth: 540, margin: '0 auto',
          background: '#0a1b36', border: '1px solid #30404e',
          borderRadius: '16px 16px 0 0', padding: '20px 20px 32px',
          fontFamily: "'DM Sans', sans-serif",
          animation: 'slideUp 0.2s ease',
        }}
      >
        <div style={{ width: 40, height: 4, borderRadius: 2, background: '#30404e', margin: '0 auto 16px' }} />
        <div style={{ fontSize: 16, fontWeight: 700, color: '#f0f2f6', marginBottom: 4 }}>Reset pipeline?</div>
        <div style={{ fontSize: 13, color: '#a8b8cc', marginBottom: 6 }}>
          {sAddr(da.address) || da.da_number}
        </div>
        <div style={{ fontSize: 13, color: '#6a80a0', marginBottom: 20 }}>
          This will clear all pipeline data for this DA and return it to Watching.
        </div>
        <button
          onClick={confirm}
          disabled={saving}
          style={{
            width: '100%', padding: '13px 16px',
            background: 'linear-gradient(135deg, #d4a84a, #b08830)',
            border: 'none', borderRadius: 10,
            color: '#081525', fontSize: 15, fontWeight: 700,
            cursor: saving ? 'wait' : 'pointer',
            fontFamily: "'DM Sans', sans-serif",
            opacity: saving ? 0.7 : 1,
          }}
        >
          {saving ? 'Resetting…' : 'Reset to Watching'}
        </button>
        <button
          onClick={onClose}
          style={{
            width: '100%', marginTop: 8, padding: '13px 16px',
            background: '#0a1b36', border: '1px solid #30404e',
            borderRadius: 10, color: '#f0f2f6', fontSize: 15, fontWeight: 600,
            cursor: 'pointer', fontFamily: "'DM Sans', sans-serif",
          }}
        >Cancel</button>
      </div>
    </div>
  );
}

function AdvanceSheet({ da, targetStage, updateStage, onClose }) {
  const [saving, setSaving] = useState(false);
  const followUpDateRef = useRef(null);
  const [fields, setFields] = useState({
    contact_name: da.contact_name || '',
    contact_method: da.contact_method || '',
    quoted_amount: da.quoted_amount || '',
    won_amount: da.won_amount || da.quoted_amount || '',
    next_follow_up_at: da.next_follow_up_at || '',
    follow_up_reason: da.follow_up_reason || '',
  });
  const col = PIPELINE_COLORS[targetStage] || PIPELINE_COLORS.watching;
  const fromStage = da.pipeline_stage || 'watching';

  const inputStyle = {
    width: '100%', boxSizing: 'border-box',
    background: '#0a1b36', border: '1px solid #30404e',
    borderRadius: 8, color: '#f0f2f6', fontSize: 'var(--text-input, 16px)',
    fontFamily: "'DM Sans', sans-serif", padding: '10px 12px',
    outline: 'none', marginTop: 8,
  };
  const labelStyle = {
    fontSize: 11, fontWeight: 700, textTransform: 'uppercase',
    letterSpacing: 0.5, color: '#a8b8cc', marginTop: 12, display: 'block',
  };

  const confirm = async () => {
    if (saving) return;
    setSaving(true);
    const extra = {};
    const followUpDate = followUpDateRef.current?.value || fields.next_follow_up_at || null;
    if (targetStage === 'contacted') {
      extra.contact_name = fields.contact_name.trim() || null;
      extra.contact_method = fields.contact_method.trim() || null;
      extra.next_follow_up_at = followUpDate;
      extra.follow_up_reason = fields.follow_up_reason.trim() || null;
    }
    if (targetStage === 'quoted') {
      const amt = parseFloat(fields.quoted_amount);
      if (!isNaN(amt) && amt > 0) extra.quoted_amount = amt;
      extra.next_follow_up_at = followUpDate;
      extra.follow_up_reason = fields.follow_up_reason.trim() || null;
    }
    if (targetStage === 'won') {
      const amt = parseFloat(fields.won_amount);
      if (!isNaN(amt) && amt > 0) extra.won_amount = amt;
      extra.next_follow_up_at = null;
      extra.follow_up_reason = null;
    }
    await updateStage(da.tracked_id, fromStage, targetStage, extra);
    setSaving(false);
    onClose();
  };

  return (
    <div
      onClick={onClose}
      style={{ position: 'fixed', inset: 0, zIndex: 2000, background: 'rgba(0,0,0,0.65)', display: 'flex', alignItems: 'flex-end' }}
    >
      <style>{`@keyframes slideUp { from { transform: translateY(60px); opacity: 0; } to { transform: translateY(0); opacity: 1; } }`}</style>
      <div
        onClick={e => e.stopPropagation()}
        style={{
          width: '100%', maxWidth: 540, margin: '0 auto',
          background: '#0a1b36', border: '1px solid #30404e',
          borderRadius: '16px 16px 0 0', padding: '20px 20px 32px',
          fontFamily: "'DM Sans', sans-serif",
          animation: 'slideUp 0.2s ease',
        }}
      >
        <div style={{ width: 40, height: 4, borderRadius: 2, background: '#30404e', margin: '0 auto 16px' }} />
        <div style={{ fontSize: 16, fontWeight: 700, color: '#f0f2f6', marginBottom: 4 }}>
          Move to {col.label}
        </div>
        <div style={{ fontSize: 13, color: '#a8b8cc', marginBottom: 10 }}>
          {sAddr(da.address) || da.da_number}
        </div>

        {targetStage === 'contacted' && (
          <>
            <label style={labelStyle}>Contact name</label>
            <input
              type="text"
              value={fields.contact_name}
              onChange={e => setFields(f => ({ ...f, contact_name: e.target.value }))}
              placeholder="e.g. Sarah Chen"
              style={inputStyle}
            />
            <label style={labelStyle}>Contact method</label>
            <input
              type="text"
              value={fields.contact_method}
              onChange={e => setFields(f => ({ ...f, contact_method: e.target.value }))}
              placeholder="e.g. email, phone, in person"
              style={inputStyle}
            />
          </>
        )}

        {targetStage === 'quoted' && (
          <>
            <label style={labelStyle}>Quoted amount</label>
            <input
              type="number"
              value={fields.quoted_amount}
              onChange={e => setFields(f => ({ ...f, quoted_amount: e.target.value }))}
              placeholder="e.g. 450000"
              style={inputStyle}
            />
          </>
        )}

        {targetStage === 'won' && (
          <>
            <label style={labelStyle}>Won amount</label>
            <input
              type="number"
              value={fields.won_amount}
              onChange={e => setFields(f => ({ ...f, won_amount: e.target.value }))}
              placeholder="e.g. 450000"
              style={inputStyle}
            />
          </>
        )}

        {!['won', 'lost', 'not_relevant'].includes(targetStage) && (
          <>
            <label style={labelStyle}>Next follow-up</label>
            <input
              type="date"
              ref={followUpDateRef}
              value={fields.next_follow_up_at}
              onChange={e => setFields(f => ({ ...f, next_follow_up_at: e.target.value }))}
              onInput={e => setFields(f => ({ ...f, next_follow_up_at: e.target.value }))}
              style={inputStyle}
            />
            <label style={labelStyle}>Follow-up note</label>
            <input
              type="text"
              value={fields.follow_up_reason}
              onChange={e => setFields(f => ({ ...f, follow_up_reason: e.target.value }))}
              placeholder="e.g. call builder after exhibition closes"
              style={inputStyle}
            />
          </>
        )}

        <button
          onClick={confirm}
          disabled={saving}
          style={{
            width: '100%', marginTop: 20, padding: '13px 16px',
            background: 'linear-gradient(135deg, #d4a84a, #b08830)',
            border: 'none', borderRadius: 10,
            color: '#081525', fontSize: 15, fontWeight: 700,
            cursor: saving ? 'wait' : 'pointer',
            fontFamily: "'DM Sans', sans-serif",
            opacity: saving ? 0.7 : 1,
          }}
        >
          {saving ? 'Saving...' : `Confirm - ${col.label}`}
        </button>
        <button
          onClick={onClose}
          style={{
            width: '100%', marginTop: 8, padding: '13px 16px',
            background: '#0a1b36', border: '1px solid #30404e',
            borderRadius: 10, color: '#f0f2f6', fontSize: 15, fontWeight: 600,
            cursor: 'pointer', fontFamily: "'DM Sans', sans-serif",
          }}
        >Cancel</button>
      </div>
    </div>
  );
}

// ── DA row with advance button + reset ────────────────────────────────────────
function DARow({ da, updateStage, onAdvanceRequest, onResetRequest, user }) {
  const stage = da.pipeline_stage || 'watching';
  const col   = PIPELINE_COLORS[stage];
  const val   = pipelineValue(da);
  const days  = daysInStage(da);
  const followUp = followUpMeta(da.next_follow_up_at);
  const note = da.note_text || da.notes || da.follow_up_reason || '';

  const nextStage = nextStageFor(stage);

  const canReset = stage !== 'watching' && da.tracked_id;

  return (
    <div style={{
      background: '#0a1b36', border: '1px solid #30404e',
      borderRadius: 10, padding: '12px 14px', marginBottom: 6,
      display: 'flex', alignItems: 'flex-start', gap: 10,
    }}>
      {/* Stage dot */}
      <div style={{ width: 10, height: 10, borderRadius: '50%', background: col.dot, flexShrink: 0, marginTop: 4 }} />

      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 13, fontWeight: 600, color: '#f0f2f6', marginBottom: 2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {sAddr(da.address) || da.da_number}
        </div>
        <div style={{ display: 'flex', gap: 8, fontSize: 11, color: '#a8b8cc', flexWrap: 'wrap', alignItems: 'center' }}>
          {da.da_number && <span style={{ fontFamily: "'JetBrains Mono',monospace" }}>{da.da_number}</span>}
          {val > 0 && <><span style={{ opacity: 0.4 }}>·</span><span style={{ color: col.text, fontWeight: 700 }}>{fmt(val)}</span></>}
          {days && <><span style={{ opacity: 0.4 }}>·</span><span>{days} in stage</span></>}
          {stage === 'quoted' && da.quoted_amount && <><span style={{ opacity: 0.4 }}>·</span><span>Quoted {fmt(da.quoted_amount)}</span></>}
          {followUp && (
            <>
              <span style={{ opacity: 0.4 }}>·</span>
              <span style={{ color: followUp.urgent ? '#d06868' : '#d4a84a', fontWeight: 700 }}>{followUp.label}</span>
            </>
          )}
          {canReset && (
            <>
              <span style={{ opacity: 0.4 }}>·</span>
              <button
                onClick={e => { e.stopPropagation(); onResetRequest(da); }}
                style={{
                  background: 'none', border: 'none',
                  color: '#6a80a0', fontSize: 11, cursor: 'pointer',
                  padding: 0, fontFamily: "'DM Sans', sans-serif",
                  lineHeight: 'inherit',
                }}
              >Reset</button>
            </>
          )}
        </div>
        {note && (
          <div style={{ marginTop: 6, fontSize: 11, color: '#8ea0b8', lineHeight: 1.35 }}>
            {note.length > 96 ? `${note.slice(0, 95)}…` : note}
          </div>
        )}
        <ProSignal da={da} user={user} />
      </div>

      {/* Quick-advance button */}
      {nextStage && updateStage && da.tracked_id && (
        <button
          onClick={() => onAdvanceRequest(da, nextStage)}
          style={{
            flexShrink: 0, padding: '5px 10px', borderRadius: 6,
            background: 'transparent', border: `1px solid ${PIPELINE_COLORS[nextStage].dot}55`,
            color: PIPELINE_COLORS[nextStage].text, fontSize: 11, fontWeight: 600,
            cursor: 'pointer', fontFamily: "'DM Sans', sans-serif", whiteSpace: 'nowrap',
          }}
        >
          → {PIPELINE_COLORS[nextStage].label}
        </button>
      )}
    </div>
  );
}

function CollapsibleGroup({ stage, das, updateStage, onAdvanceRequest, onResetRequest, defaultOpen, user }) {
  const [open, setOpen] = useState(defaultOpen);
  const col   = PIPELINE_COLORS[stage];
  const total = das.reduce((s, da) => s + pipelineValue(da), 0);

  return (
    <div style={{ marginBottom: 8 }}>
      <button
        onClick={() => setOpen(o => !o)}
        style={{ display: 'flex', alignItems: 'center', gap: 8, width: '100%', background: 'none', border: 'none', padding: '8px 0', cursor: 'pointer', textAlign: 'left' }}
      >
        <svg width="12" height="12" viewBox="0 0 12 12" fill="none"
          style={{ transform: open ? 'rotate(90deg)' : 'rotate(0deg)', transition: 'transform 0.2s', flexShrink: 0 }}>
          <path d="M4 2l4 4-4 4" stroke="#6a80a0" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
        <span style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5, color: col.text }}>
          {col.label}
        </span>
        <span style={{ fontSize: 11, color: '#6a80a0' }}>· {das.length}</span>
        {total > 0 && <span style={{ fontSize: 11, color: '#6a80a0', marginLeft: 'auto' }}>{fmt(total)}</span>}
      </button>
      {open && (
        <div>
          {das.map(da => (
            <DARow
              key={da.id || da.tracked_id}
              da={da}
              updateStage={updateStage}
              onAdvanceRequest={onAdvanceRequest}
              onResetRequest={onResetRequest}
              user={user}
            />
          ))}
        </div>
      )}
    </div>
  );
}

export default function PipelineSummaryView({ tracked, updateStage, theme }) {
  const [resetTarget, setResetTarget] = useState(null);
  const [advanceTarget, setAdvanceTarget] = useState(null);
  const { user } = useAuth();

  const groups = {};
  for (const stage of STAGE_ORDER) groups[stage] = [];
  for (const da of tracked) {
    const s = da.pipeline_stage || 'watching';
    if (groups[s]) groups[s].push(da);
  }

  const stats = STAGE_ORDER.map(stage => {
    const das   = groups[stage];
    const total = das.reduce((s, da) => s + pipelineValue(da), 0);
    return { stage, count: das.length, total };
  });
  const coreStats = stats.filter(({ stage }) => CORE_STAGE_ORDER.includes(stage));
  const progressTotalValue = coreStats.reduce((s, stat) => s + stat.total, 0);
  const progressTotalCount = coreStats.reduce((s, stat) => s + stat.count, 0);
  const progressUsesValue = progressTotalValue > 0;
  const progressHasData = progressUsesValue ? progressTotalValue > 0 : progressTotalCount > 0;

  return (
    <div style={{ padding: '12px 16px 32px', fontFamily: "'DM Sans', sans-serif" }}>

      <p style={{ color: theme.textMuted, fontSize: 12 }}>All tracked projects. Watching/contacted values are declared development budgets; quoted/won values use your recorded amounts only.</p>
      {/* Summary stat cards */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(4, minmax(0, 1fr))',
        gap: 6,
        marginBottom: 10,
      }}>
        {coreStats.map(({ stage, count, total }) => {
          const col = PIPELINE_COLORS[stage];
          return (
            <div key={stage} style={{
              minWidth: 0, background: '#0a1b36', border: '1px solid #30404e',
              borderRadius: 10, padding: '8px 10px',
              boxShadow: '0 2px 8px rgba(0,0,0,0.3)',
            }}>
              <div style={{ fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5, color: '#6a80a0', marginBottom: 4 }}>
                {col.label}
              </div>
              <div style={{ fontSize: 20, fontWeight: 800, color: col.text, lineHeight: 1 }}>{count}</div>
              {total > 0 && <div style={{ fontSize: 10, color: '#a8b8cc', marginTop: 3 }}>{fmt(total)}</div>}
            </div>
          );
        })}
      </div>

      {/* Customer-facing four-stage pipeline progress */}
      <div style={{ marginBottom: 20 }}>
        <div style={{
          height: 8,
          borderRadius: 999,
          overflow: 'hidden',
          background: '#071326',
          border: '1px solid #243552',
          display: 'flex',
          gap: 1,
        }}>
          {progressHasData ? coreStats.map(({ stage, count, total }) => {
            const weight = progressUsesValue ? total : count;
            if (weight <= 0) return null;
            return (
              <div
                key={stage}
                title={`${PIPELINE_COLORS[stage].label}: ${count}${total > 0 ? ` / ${fmt(total)}` : ''}`}
                style={{
                  flex: `${weight} 1 0`,
                  minWidth: 10,
                  background: PIPELINE_COLORS[stage].dot,
                }}
              />
            );
          }) : (
            <div style={{ width: '100%', background: '#182337' }} />
          )}
        </div>
        <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8, marginTop: 6, flexWrap: 'wrap' }}>
          {coreStats.map(({ stage, count }) => (
            <div key={stage} style={{ display: 'flex', alignItems: 'center', gap: 5, minWidth: 0 }}>
              <span style={{ width: 6, height: 6, borderRadius: '50%', background: PIPELINE_COLORS[stage].dot, flexShrink: 0 }} />
              <span style={{ fontSize: 10, color: '#6a80a0', whiteSpace: 'nowrap' }}>
                {PIPELINE_COLORS[stage].label} {count}
              </span>
            </div>
          ))}
        </div>
      </div>

      {/* Grouped DA lists */}
      {tracked.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '40px 0', color: '#6a80a0', fontSize: 14 }}>
          No tracked DAs yet.
        </div>
      ) : (
        STAGE_ORDER.map(stage =>
          groups[stage].length > 0 && (
            <CollapsibleGroup
              key={stage}
              stage={stage}
              das={groups[stage]}
              updateStage={updateStage}
              onAdvanceRequest={(da, targetStage) => setAdvanceTarget({ da, targetStage })}
              onResetRequest={setResetTarget}
              user={user}
              defaultOpen={!['lost', 'not_relevant'].includes(stage)}
            />
          )
        )
      )}

      {/* Reset confirmation sheet — rendered at PipelineSummaryView level so it's above everything */}
      {resetTarget && (
        <ResetSheet
          da={resetTarget}
          updateStage={updateStage}
          onClose={() => setResetTarget(null)}
        />
      )}
      {advanceTarget && (
        <AdvanceSheet
          da={advanceTarget.da}
          targetStage={advanceTarget.targetStage}
          updateStage={updateStage}
          onClose={() => setAdvanceTarget(null)}
        />
      )}
    </div>
  );
}
